// FIXME: fix these
/* eslint-disable react-compiler/react-compiler */

import * as React from "react";

import AutoComplete from "@/game/AutoComplete";
import ErrorPrompt from "@/components/shared/ErrorPrompt";
import CreateHeadlineTimer from "@/components/CreateHeadlineTimer";
import InGameTopNav from "@/components/InGameTopNav";
import JaggedButton from "@/components/JaggedButton";
import Swipeable from "@/components/Swipeable";
import * as Random from "@/realize-shared/Random";
import { MIN_HEADLINE_LENGTH } from "@/data/Config";
import * as PlayerRequests from "@/api/PlayerRequests";
import * as Sanitization from "@/realize-shared/Sanitization";
import { sanitizeInputElement } from "@/util/ElementSanitation";
import type * as ReactTypes from "@/game/types/ReactTypes";

import IconChevron from "@/assets/img/symbolic/chevron-left.svg?react";
import IconClose from "@/assets/img/symbolic/close.svg?react";
import IconPencil from "@/assets/img/symbolic/pencil.svg?react";
import IconDice from "@/assets/img/symbolic/dice.svg?react";
import SendIcon from "@/assets/img/chat/send.svg?react";
import SpaceImage from "@/assets/img/backgrounds/space.webp";
import BGTexture from "@/assets/img/backgrounds/hatches.webp";
import RightButtonBG from "@/assets/img/layout/game/right-swipe-bg.png";
import LeftButtonBG from "@/assets/img/layout/game/left-swipe-bg.png";
import Padding from "@/components/shared/Padding";
import GlobalGUI from "@/util/GlobalGUI";
import { useGameContext } from "@/game/GameContext";
import { GameUIState } from "@/game/GameUIState";
import { useAppContext } from "@/game/AppContext";
import ThemeContext from "@/game/themes/ThemeContext";
import { Themes } from "@/game/themes/Themes";

const MAX_INPUT_LENGTH = 100;

type Handles<T> = Record<string, T | null>;

/**
 * Scrollable pop-up to select single word
 */
const WordHelperPopup = (props: {
    setShowOptions: ReactTypes.SetState<boolean>;
    scrollingArea: React.RefObject<HTMLDivElement>;
    options: string[];
    setSelectedOption: (option: number) => void;
    selector: React.RefObject<HTMLDivElement>;
}) =>
{
    const {
        setShowOptions,
        scrollingArea,
        options,
        setSelectedOption,
        selector,
    } = props;

    const [tempSelected, setTempSelected] = React.useState(0);

    const setDataID = (index: number) =>
    {
        if (!selector.current) return;

        // @FIXME I don't know how to fix this error
        // I think believe it is a side-effect of using forwardRef, which is in the process of being deprecated

        selector.current.dataset.id = index.toString();
    };

    const getWordlist = () =>
    {
        let previousLetter = "";

        return options.map((option, index) =>
        {
            const firstLetterOfWord = option
                .replaceAll(/\*/ug, "")
                .replace(/^(?:an?|the) /giu, "")[0];

            const newLetter = firstLetterOfWord !== previousLetter;
            const selectedClasses = (index === tempSelected)? "bg-sharp-red bg-opacity-10": "";

            if (newLetter) previousLetter = firstLetterOfWord;

            return (
                <div
                    key={index}
                    className={
                        `text-dark-blue font-default font-semibold
                        text-base py-1 leading-6 ${selectedClasses}`
                    }
                    onClick={() => void setTempSelected(index)}
                >
                    {newLetter && (
                        <div
                            className={
                                `float-left uppercase text-secondary-gray font-default
                                text-base font-bold leading-6 ml-2 relative w-4`
                            }
                            id={`letter-${firstLetterOfWord.toLowerCase()}`}
                        >
                            {firstLetterOfWord}
                        </div>
                    )}
                    <div
                        className={`${newLetter ? "-ml-6 w-full absolute" : ""}}`}
                    >
                        {option.replace(/\*/gu, "")}
                    </div>
                </div>
            );
        });
    };

    return (
        <div
            className="fixed inset-0 bg-black z-50 bg-opacity-75"
            onClick={(e) =>
            {
                if (e.target === e.currentTarget)
                {
                    setShowOptions(false);

                    // Remove the pop-over class from the body. This shows the chat.
                    document.body.classList.remove("pop-over");
                }
            }}
        >
            <div
                className={`
                    bg-parchment
                    w-full
                    absolute
                    bottom-0
                    pb-[var(--safe-area-bottom)]
                    h-[75vh]
                    shadow-[0px_-5px_15px_-5px_rgba(0,0,0,0.5)]
                    flex
                    flex-col
                `}
            >
                <h2
                    className={
                        "py-2 px-3 text-dark-blue font-bold leading-normal text-left"
                    }
                >
                    Word Selection
                </h2>
                <div
                    className="py-2 overflow-y-auto flex flex-col gap-2"
                    ref={scrollingArea}
                >
                    {getWordlist()}
                </div>
                <div className="py-4 flex items-center justify-end px-3 gap-2">
                    <button
                        className={
                            `bg-dark-blue rounded lowercase text-white
                            text-base font-default font-normal py-2 w-32`
                        }
                        onClick={() =>
                        {
                            setShowOptions(false);

                            // Remove the pop-over class from the body. This shows the chat.
                            document.body.classList.remove("pop-over");
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        className={
                            `bg-sharp-red rounded text-white lowercase
                            text-base font-default font-normal py-2 w-32`
                        }
                        onClick={() =>
                        {
                            setDataID(tempSelected);
                            setSelectedOption(tempSelected);
                            setShowOptions(false);

                            // Remove the pop-over class from the body. This shows the chat.
                            document.body.classList.remove("pop-over");
                        }}
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * Text entry pop-up to allow user to type in custom headline
 */
const HeadlineTextInputContainer = ({
    visible,
    hide,
    submit,
    headline,
}: {
    visible: boolean;
    hide: () => unknown;
    headline: string;
    submit: (headline: string) => unknown;
}) =>
{
    const inputRef = React.useRef<HTMLTextAreaElement>(null);

    // grow/shrink to show text
    const reflow = () =>
    {
        if (!inputRef.current) return;

        inputRef.current.style.height = "0";
        inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    };

    // heighlight input
    React.useMemo(() =>
    {
        if (!inputRef.current || !visible) return;

        const text = headline.charAt(0).toUpperCase() + headline.slice(1);

        inputRef.current.value = text;

        inputRef.current.focus();
        inputRef.current.setSelectionRange(text.length, text.length);

        reflow();
    }, [visible, headline]);

    const send = () =>
    {
        const input = inputRef.current;

        if (!input) return;

        const length = input.value.length;

        if (length > MAX_INPUT_LENGTH) return;

        submit(sanitizeInputElement(
            input,
            Sanitization.sanitizeTextBlock,
        ));
    };

    return (
        <div
            className={`
                fixed
                inset-0
                bg-black
                z-50
                backdrop-blur
                transition-opacity
                duration-100
                ${visible && "opacity-80"}
                ${visible? "block": "invisible"}
            `}
            id="custom-headline-backdrop"
            onClick={(e) =>
            {
                if (e.target === e.currentTarget) hide();
            }}
        >
            <Padding
                top
                bottom
                wrapper={Wind.style({
                    position: "relative",
                    width: "w-full",
                    height: "h-full",
                    backgroundColor: "bg-black",
                    opacity: "opacity-100",
                }).class}
                onClick={(e) =>
                {
                    if (e.target === e.currentTarget) hide();

                    const element = e.target as HTMLDivElement;

                    if (element.classList.contains("padding-wrapper")) hide();
                }}
            >
                <button
                    className={`
                        absolute
                        block
                        right-4
                        mt-4
                        px-4
                        py-2
                        cancelButton
                        z-[1000]
                        text-white
                    `}
                    style={{
                        "paddingTop": "var(--safe-area-top)",
                    }}
                    onClick={hide}
                >
                    <IconClose
                        className={`
                            w-8
                            h-8
                        `}
                    />
                </button>
                <div
                    className={`
                        absolute
                        px-4
                        w-full
                    `}

                    style={{
                        bottom: "calc(var(--keyboard-height) + 1rem)"
                    }}
                >
                    <div
                        className={`
                            flex
                            justify-between
                            items-center
                        `}
                    >
                        <p
                            className={`
                                mb-1
                                text-white
                                text-opacity-70
                                text-xs
                            `}
                        >
                            Type in a Headline:
                        </p>
                        <div
                            className={`
                                flex
                                items-center
                                text-white
                                text-opacity-70
                            `}
                        >
                            {inputRef.current?.value.length ?? 0}
                            {" / "}
                            {MAX_INPUT_LENGTH}
                        </div>
                    </div>
                    <div
                        className={`
                            p-2
                            border
                            border-white
                            flex
                        `}
                    >
                        <textarea
                            className={`
                                flex-[1_1_100%]
                                bg-transparent
                                placeholder:text-left
                                text-white
                                border-none
                                focus:outline-none
                            `}

                            placeholder="Realize..."
                            name="headline"
                            inputMode="text"
                            rows={1}

                            ref={inputRef}

                            onInput={(e) =>
                            {
                                e.currentTarget.value = sanitizeInputElement(
                                    e.currentTarget,
                                    Sanitization.sanitizeTextBlock,
                                );

                                if (e.currentTarget.value.length > MAX_INPUT_LENGTH)
                                {
                                    e.currentTarget.value = e.currentTarget.value.slice(0, MAX_INPUT_LENGTH);
                                }

                                reflow();
                            }}

                            onKeyDown={(e) =>
                            {
                                if (e.key !== "Enter") return;

                                send();
                            }}
                        />
                        <button
                            className="
                                text-black
                                bg-white
                                px-2
                                rounded-lg
                            "
                            onClick={() =>
                            {
                                send();
                            }}
                        >
                            <SendIcon />
                        </button>
                    </div>
                </div>
            </Padding>
        </div>
    );
};

/**
 * Yellow highlighted word in mad-lib headline
 */
const Highlighted = (props: {
    text: string;
}) =>
{
    const { text } = props;

    // skip "*", "**", and any word NOT surrounded by "*"
    const plain = text.length <= 2 || !text.startsWith("*") || !text.endsWith("*");

    if (plain) return text;

    const stripped = text.substring(2, text.length-2);

    return (
        <span className="font-bold">
            {stripped}
        </span>
    );
};

interface AutoFillSectionHandle
{
    getIndex: () => number;
    getSection: () => Word;
    setSection: (section: Word) => void;
}
type AutoFillSectionHandles = Handles<AutoFillSectionHandle>;

const AutoFillSection = React.forwardRef((props: {
    section: Word;
    self: number;
    setSelected: (index: number) => void;
}, ref: React.Ref<AutoFillSectionHandle>) =>
{
    const {
        setSelected,
    } = props;

    const scrollingArea = React.useRef<HTMLDivElement>(null);
    const selector = React.useRef<HTMLDivElement>(null);

    const [section, setSection] = React.useState(props.section);
    const [showOptions, setShowOptions] = React.useState(false);

    React.useImperativeHandle(ref, () => ({
        setSection,
        "getIndex":   () => props.self,
        "getSection": () => section,
    }));

    React.useEffect(() =>
    {
        // Auto Scroll To Selected Option
        const element = scrollingArea.current?.children[section[1]];

        if (!element) return;

        element.scrollIntoView({
            "behavior": "auto",
            "block":    "center",
            "inline":   "center",
        });
    }, [showOptions, section]);

    return (
        <>
            <div
                className={
                    `inline-block mx-1 bg-poppy-yellow text-dark-blue px-4 my-1
                    uppercase font-normal text-2xl leading-[110%] auto-fill-input`
                }
                onClick={() =>
                {
                    setShowOptions(!showOptions);

                    // Add the pop-over class to the body. This hides the chat.
                    if (!document.body.classList.contains("pop-over"))
                    {
                        document.body.classList.add("pop-over");
                    }
                }}
                data-id={section[1]}
                ref={selector}
            >
                <Highlighted text={section[0][section[1]]} />
            </div>
            {showOptions && (
                <WordHelperPopup
                    setShowOptions={setShowOptions}
                    scrollingArea={scrollingArea}
                    options={section[0]}
                    setSelectedOption={(index) =>
                    {
                        setSelected(index);
                    }}
                    selector={selector}
                />
            )}
        </>
    );
});

AutoFillSection.displayName = "AutoFillSection";

interface HeadlinePromptHandle
{
    reroll: () => void;
    getTemplate: () => Template;
}
type HeadlinePromptHandles = Record<string, HeadlinePromptHandle | null>;

const HeadlinePrompt = React.forwardRef((props: {
    template: Template;
    self: React.Key;
    update: (template: Template) => void;
}, ref: React.Ref<HeadlinePromptHandle>) =>
{
    const {
        self,
        template,
        update,
    } = props;

    const [elements, setElements] = React.useState<React.JSX.Element[]>([]);
    const wordRefs = React.useRef<AutoFillSectionHandles>({});

    const fillInWord = React.useCallback((options: Word, index: number, key: string) =>
    {
        return (
            <AutoFillSection
                section={options}
                setSelected={(selected: number) =>
                {
                    const current: Section = [options[0], selected];

                    wordRefs.current[key]?.setSection(current);

                    // @TODO: overhaul this component

                    template[index] = current;

                    update(template);
                }}
                key={key}
                self={index}
                ref={(element) => (wordRefs.current[key] = element)}
            />
        );
    }, [template, update]);

    const rawString = (element: string, key: React.Key) => (
        <span
            key={key}
            className={
                "font-default text-white text-2xl font-medium"
            }
            style={{
                "textShadow": "0px 2px 2px rgba(0, 0, 0, 0.40)",
            }}
        >
            {element}
        </span>
    );

    const asElement = React.useCallback((element: Section, index: number) =>
    {
        const key = `${self},${index}`;

        return (Array.isArray(element))
            ? fillInWord(element, index, key)
            : rawString(element, key);
    }, [fillInWord, self]);

    React.useImperativeHandle(ref, () => ({
        reroll ()
        {
            Object.values(wordRefs.current).forEach((word) =>
            {
                if (!word) return;

                const index = word.getIndex();
                const prev = word.getSection();
                const current: Section = [prev[0], Random.integer(prev[0].length)];

                word.setSection(current);

                template[index] = current;
            });

            update(template);
        },
        getTemplate ()
        {
            return template;
        },
    }));

    React.useMemo(() =>
    {
        if (template.length === 0) throw new Error("Empty template");

        setElements(template.map(asElement));
    }, [asElement, template]);

    return (
        <div className="w-full py-6 mb-4">
            <div className="text-center px-2 first-letter:uppercase">
                {elements}
            </div>
        </div>
    );
});

HeadlinePrompt.displayName = "HeadlinePrompt";

type Word = [suggestions: string[], selected: number];
type Section = string | Word;
type Template = Section[];

const parseTemplate = (section: string): Section =>
{
    const parsed = AutoComplete.get(section);

    return (parsed)
        ? [parsed, Random.integer(parsed.length)] // interactive
        : section; // non-interactive
};

const templateToString = (template: Template) => template
    .map((section) =>
    {
        if (typeof section === "string") return section;

        return section[0][section[1]];
    })
    .join(" ");

const HeadlineCreation = () =>
{
    const { room } = useGameContext();
    const { setWaiting } = useAppContext();

    const [headline, setHeadline] = React.useState("");

    const [showHeadlineHelpers, setShowHeadlineHelpers] = React.useState(true);
    const [error, setError] = React.useState("");

    const [history, setHistory] = React.useState<React.JSX.Element[]>([]);
    const [count, setCount] = React.useState<number>(0);
    const [displayed, setDisplayed] = React.useState<number>(-1);

    const headlineRefs = React.useRef<HeadlinePromptHandles>({});

    const getHeadline = React.useCallback(() =>
    {
        const index = history.length;
        const rawTemplate = AutoComplete.templates[Random.integer(AutoComplete.templates.length)];

        let template = rawTemplate.map(parseTemplate);

        const phrase = (
            <HeadlinePrompt
                ref={(element) => (headlineRefs.current[index] = element)}
                key={count}
                self={count} // same as key
                template={template}
                update={(current: Template) =>
                {
                    template = [...current];

                    changeHeadline(template);
                }}
            />
        );

        setCount(count+1);
        setDisplayed(index);

        return phrase;
    }, [count, history]);

    const pushHeadline = React.useCallback(() =>
    {
        setHistory([...history, getHeadline()]);
    }, [getHeadline, history]);

    const previousHeadline = () =>
    {
        setDisplayed(Math.max(displayed-1, 0));
    };

    const nextHeadline = () =>
    {
        if (displayed >= history.length - 1)
        {
            pushHeadline();
        }
        else
        {
            setDisplayed(displayed + 1);
        }
    };

    const submitHeadline = (supplied?: string) =>
    {
        const output = (supplied ?? headline).trim();

        Logger.log("submit", output);

        if (output.length <= MIN_HEADLINE_LENGTH)
        {
            return void setError("Name must be at least 3 characters long");
        }

        setWaiting(true);

        void PlayerRequests.submitHeadlineRequest(room.id, output)
            .then(() => void room.data.write({gameUIState: GameUIState.CHAT}, true))
            .finally(() => void setWaiting(false));
    };

    const changeHeadline = (
        input: string | Template,
    ) =>
    {
        if (typeof input !== "string") input = templateToString(input);

        input = Sanitization.sanitizeTextBlock(input);

        Logger.log(input);

        setHeadline(input);

        return input;
    };

    const mixWords = () =>
    {
        headlineRefs.current[displayed]?.reroll();
    };

    React.useMemo(() =>
    {
        // @TODO: overhaul this component

        if (history.length <= 0) pushHeadline();
    }, [history.length, pushHeadline]);

    React.useEffect(() =>
    {
        const template = headlineRefs.current[displayed]?.getTemplate();

        if (!template) return;

        changeHeadline(template);
    }, [displayed]);

    React.useEffect(() =>
    {
        const resetStyle = GlobalGUI.setStyle({
            statusbarStyle: "dark",
        });

        return () =>
        {
            resetStyle();

            // On unmount remove pop-over
            document.body.classList.remove("pop-over");
        };
    }, []);

    return (
        <ThemeContext.Provider value={Themes.get("default-theme").data}>
            <div
                className="pt-[--safe-area-top]"
                style={{
                    "height": "100%",
                }}
            >
                <InGameTopNav
                    hideTimer
                    onReturnToHome={() => void room.data.write({gameUIState: GameUIState.CHAT}, true)}
                />
                <div
                    className={
                        `h-full flex items-center justify-start flex-col bg-cover
                        flex-grow
                        bg-center bg-no-repeat`
                    }
                >
                    <div
                        className="
                            fixed
                            inset-0
                            bg-[#465362]
                            bg-cover
                            bg-no-repeat
                        "
                        style={{
                            "backgroundImage": `url(${BGTexture})`,
                        }}
                    />
                    <div
                        className="
                            fixed
                            w-full
                            rotate-2
                            h-auto
                            aspect-[810_/_1473]
                        "
                    >
                        <div
                            className="
                                translate-x-[3px]
                                translate-y-[3px]
                                bg-[#13181B]
                                absolute
                                w-full
                                h-full
                            "
                        />
                        <div
                            className="
                                translate-x-[2px]
                                translate-y-[2px]
                                bg-[#5A5E5E]
                                absolute
                                w-full
                                h-full
                            "
                        />
                        <div
                            className="
                                translate-x-[1px]
                                translate-y-[1px]
                                bg-[#242A2E]
                                absolute
                                w-full
                                h-full
                            "
                        />
                        <div
                            className="
                                bg-black
                                absolute
                                w-full
                                h-full
                                bg-cover
                                bg-no-repeat
                            "
                            style={{
                                "backgroundImage": `url(${SpaceImage})`,
                            }}
                        />
                    </div>
                    <div className="fixed inset-0 bg-black bg-opacity-50" />
                    <div className="mx-9 pt-14 flex flex-col flex-grow h-full relative">
                        <CreateHeadlineTimer />
                        <HeadlineTextInputContainer
                            visible={!showHeadlineHelpers}
                            hide={() =>
                            {
                                setShowHeadlineHelpers(true);
                                document.body.classList.remove("pop-over");
                            }}
                            headline={headline}
                            submit={submitHeadline}
                        />
                        <Swipeable
                            className="-mx-9 px-4 relative h-48 flex items-center justify-center"
                            handlers={{
                                "onSwipedRight": previousHeadline,
                                "onSwipedLeft":  nextHeadline,
                            }}
                        >
                            <button
                                onClick={previousHeadline}
                                className={`
                                    absolute
                                    -left-3
                                    pl-3
                                    top-1/2
                                    -translate-y-1/2
                                    w-10
                                    h-10
                                    bg-contain
                                    bg-no-repeat
                                    bg-right
                                    text-right
                                    ${displayed > 0? "text-white": "text-gray-500"}`
                                }
                                style={{
                                    "backgroundImage": `url(${LeftButtonBG})`,
                                }}
                            >
                                <IconChevron />
                            </button>
                            {history[displayed]}
                            <button
                                onClick={nextHeadline}
                                className="
                                    absolute
                                    -right-3
                                    top-1/2
                                    -translate-y-1/2
                                    w-10
                                    h-10
                                    bg-contain
                                    bg-left
                                    bg-no-repeat
                                    text-white
                                "
                                style={{
                                    "backgroundImage": `url(${RightButtonBG})`,
                                }}
                            >
                                <IconChevron className="rotate-180" />
                            </button>
                        </Swipeable>
                        <div
                            className="
                                flex
                                flex-col
                                flex-grow
                                items-center
                                justify-center
                                text-dark-4
                                -mt-10
                                relative
                                z-10
                            "
                        >
                            <div
                                className={
                                    `grid grid-cols-2 gap-1 text-white font-default
                                    font-medium leading-normal text-xs max-w-xs mx-auto mt-8`
                                }
                                style={{
                                    "textShadow": "0px 2px 2px rgba(0, 0, 0, 0.40)",
                                }}
                            >
                                <button
                                    className="flex items-center justify-center flex-col w-24"
                                    onClick={() =>
                                    {
                                        setShowHeadlineHelpers(false);
                                        document.body.classList.add("pop-over");
                                    }}
                                >
                                    <IconPencil className="w-8 h-8 mb-2 text-white" />
                                    Create your own Headline
                                </button>
                                <button
                                    className="flex items-center justify-center flex-col w-24"
                                    onClick={mixWords}
                                >
                                    <IconDice className="w-8 h-8 mb-2 text-white" />
                                    Reroll Filler Words
                                </button>
                            </div>
                            <div className="flex-grow w-full" />
                            <JaggedButton
                                color="text-sharp-red"
                                className={`
                                    right-0
                                    self-end
                                    -mr-8
                                    text-white
                                    font-semibold
                                    leading-[130%]
                                    text-xl
                                    px-0
                                    h-20
                                    mb-[var(--safe-area-bottom)]
                                    w-2/3
                                `}
                                onClick={() => void submitHeadline()}
                            >
                                Choose Phrase
                            </JaggedButton>
                        </div>
                    </div>
                    {error && (
                        <ErrorPrompt
                            error={error}
                            onClose={() => void setError("")}
                        />
                    )}
                </div>
            </div>
        </ThemeContext.Provider>
    );
};

export default HeadlineCreation;
